import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { tagsAPI } from '../../services/api';
import Pagination from '../../components/Pagination/Pagination';
import styles from './TagsList.module.css';

const TagsList = () => {
  const navigate = useNavigate();

  const [tags, setTags] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(80);
  const [totalItems, setTotalItems] = useState(0);

  // 获取标签数据
  const fetchTags = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await tagsAPI.getAllTags(currentPage, pageSize);

      if (response.data?.success || response.data?.code === 200) {
        const tagsData = response.data.data.records || [];
        const total = response.data.data.total || 0;
        setTotalItems(total);
        // 确保 tagsData 是数组
        if (Array.isArray(tagsData)) {
          // 为标签添加颜色
          const tagsWithColors = tagsData.map((tag, index) => ({
            id: index + 1,
            name: tag.name,
            count: tag.resourceCount,
            color: getTagColor(index)
          }));
          setTags(tagsWithColors);
        } else {
          console.error('标签数据格式错误:', tagsData);
          setError('标签数据格式错误');
        }
      } else {
        setError('获取标签列表失败');
      }
    } catch (error) {
      console.error('获取标签列表失败:', error);
      setError('获取标签列表失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  // 获取标签颜色
  const getTagColor = (index) => {
    const colors = [
      '#f1404b', '#ff6b35', '#1890ff', '#722ed1', '#52c41a',
      '#fa8c16', '#13c2c2', '#eb2f96', '#f5222d', '#389e0d'
    ];
    return colors[index % colors.length];
  };

  const filteredTags = tags.filter(tag => {
    const matchesSearch = tag.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  // 分页逻辑 - 使用服务器端分页
  const totalPages = Math.ceil(totalItems / pageSize);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleTagClick = (tagName) => {
    // 导航到搜索页面，传递标签参数
    navigate(`/search?tags=${encodeURIComponent(tagName)}`);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className={styles.tagsPage}>
        <div className="container">
          <div className={styles.loading}>
            <div className={styles.loadingSpinner}></div>
            <span>加载中...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.tagsPage}>
        <div className="container">
          <div className={styles.error}>
            <div className={styles.errorIcon}>❌</div>
            <h3>加载失败</h3>
            <p>{error}</p>
            <button onClick={fetchTags} className={styles.retryBtn}>
              重试
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.tagsPage}>
      <div className="container">
        <div className={styles.tagsHeader}>
          <h1>标签列表</h1>
          <p>浏览所有标签，快速找到你需要的资源</p>
        </div>

        <div className={styles.tagsSearch}>
          <input
            type="text"
            placeholder="搜索标签..."
            value={searchQuery}
            onChange={handleSearchChange}
            className={styles.searchInput}
          />
        </div>

        <div className={styles.tagsContent}>
          <div className={styles.tagsStats}>
            <span>
              共找到 {totalItems} 个标签
              {searchQuery && ` · 包含"${searchQuery}"`}
            </span>
          </div>

          <div className={styles.tagsGrid}>
            {filteredTags.map(tag => (
              <div
                key={tag.id}
                className={styles.tagCard}
                onClick={() => handleTagClick(tag.name)}
                style={{ borderLeftColor: tag.color }}
              >
                <div className={styles.tagInfo}>
                  <span className={styles.tagName}>{tag.name}</span>
                  <span className={styles.tagCount}>{tag.count.toLocaleString()} </span>
                </div>
                <div
                  className={styles.tagIndicator}
                  style={{ backgroundColor: tag.color }}
                ></div>
              </div>
            ))}
          </div>

          {filteredTags.length === 0 && (
            <div className={styles.noTags}>
              <div className={styles.noTagsIcon}>🏷️</div>
              <h3>没有找到相关标签</h3>
              <p>尝试使用不同的关键词</p>
            </div>
          )}

          {totalItems > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              showSizeChanger={true}
              pageSize={pageSize}
              onPageSizeChange={handlePageSizeChange}
              total={totalItems}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TagsList;